import PropTypes from 'prop-types';
import { Box, Button, CircularProgress } from '@mui/material';
import {
  Forum, Bolt, CheckCircle, InfoOutlined,
} from '@mui/icons-material';
import { brandColors } from '../../constants/brandColors';
import { softTints, cardShadow } from '../../constants/uiTokens';
import WhatsPreview from './WhatsPreview';
import Txt from './Txt';

const BENEFITS = [
  { title: 'Menos faltas', desc: 'Lembretes com confirmação reduzem ausências em até 30%.' },
  { title: 'Recepção livre', desc: 'Avisos e confirmações 100% automáticos, sem ligações.' },
  { title: 'Cliente no controle', desc: 'Botões para confirmar ou cancelar com um toque.' },
];

/**
 * WhatsAppHero — estado DESATIVADO da integração. Apresenta valor + benefícios +
 * CTA único de ativação, com a pré-visualização da conversa ao lado (desktop).
 * Tamanhos portados 1:1 do mockup (via Txt, imune ao shrink global).
 */
export default function WhatsAppHero({ previewText, previewShowButtons, onActivate, activating }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3, alignItems: { lg: 'flex-start' } }}>
      {/* Card de ativação */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          boxShadow: cardShadow,
          p: { xs: 3, sm: 4 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
          <Box sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: softTints.success.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Forum sx={{ fontSize: 28, color: brandColors.whatsapp.action }} />
          </Box>
          <Txt component="span" sx={{ fontSize: 11, fontWeight: 700, color: brandColors.whatsapp.action, bgcolor: softTints.success.bg, px: 1.25, py: 0.5, borderRadius: 999, letterSpacing: '.03em' }}>
            DESATIVADO
          </Txt>
        </Box>

        <Txt component="h2" sx={{ mt: 2.25, mb: 1, fontSize: 24, fontWeight: 700, color: 'text.primary', lineHeight: 1.25 }}>
          Confirme mais agendamentos pelo WhatsApp
        </Txt>
        <Txt sx={{ mb: 2.75, fontSize: 14.5, color: 'text.secondary', lineHeight: 1.55, maxWidth: 440 }}>
          Avise, lembre e confirme com seus clientes automaticamente — direto no canal que eles mais usam.
          Reduza faltas e ganhe tempo da recepção.
        </Txt>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, mb: 3.25 }}>
          {BENEFITS.map((b) => (
            <Box key={b.title} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <CheckCircle sx={{ fontSize: 22, color: brandColors.whatsapp.action, flexShrink: 0 }} />
              <Box>
                <Txt sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>{b.title}</Txt>
                <Txt sx={{ fontSize: 13, color: 'text.secondary' }}>{b.desc}</Txt>
              </Box>
            </Box>
          ))}
        </Box>

        <Button
          onClick={onActivate}
          disabled={activating}
          disableElevation
          startIcon={activating ? <CircularProgress size={18} color="inherit" /> : <Bolt />}
          sx={{
            bgcolor: brandColors.whatsapp.action,
            color: '#fff',
            borderRadius: '8px',
            px: 2.75,
            py: 1.5,
            fontSize: 14.5,
            fontWeight: 600,
            '&:hover': { bgcolor: '#1b9650' },
          }}
        >
          Ativar WhatsApp
        </Button>

        <Txt sx={{ mt: 2.25, fontSize: 12, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <InfoOutlined sx={{ fontSize: 16 }} />
          <span>Você recebe <Box component="strong" sx={{ color: 'text.secondary' }}>50 créditos grátis</Box> para testar ao ativar.</span>
        </Txt>
      </Box>

      {/* Pré-visualização ao lado (desktop) */}
      <Box sx={{ display: { xs: 'none', lg: 'flex' }, width: 340, flexShrink: 0, justifyContent: 'center', alignSelf: 'center' }}>
        <WhatsPreview text={previewText} showButtons={previewShowButtons} empty={!previewText?.trim()} />
      </Box>
    </Box>
  );
}

WhatsAppHero.propTypes = {
  previewText: PropTypes.string,
  previewShowButtons: PropTypes.bool,
  onActivate: PropTypes.func.isRequired,
  activating: PropTypes.bool,
};
